/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 * @author Tomas Burian (burian@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import {Debug, RenderDocument} from "js_renderer";
import {vec2, vec3} from "gl-matrix";
import {EBackgroundImageFitType, EBackgroundType} from "./scene_background_enums";
import SkyBox from "../../elements/sky_box";
import SkyDome from "../../elements/sky_dome";
import IBackgroundModule from "./scene_background_interface";
import Scene from "../../scene";
import Settings from "../settings";
import {ESunDirectionType} from "../lighting/scene_lighting_enums";

interface IBackground {
    textureUrl?: string;
    color?: vec3;
    type?: EBackgroundType;
}

interface IBackgroundImage {
    name?: string;
    source?: string;
    basePath?: string;
    scaleType?: EBackgroundImageFitType;
}

interface IBackgroundSettings {
    background?: IBackground;
    backgroundImage?: IBackgroundImage;
}

interface IBackgroundImageRuntimeSettings {
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
}

export default
class BackgroundModule extends AbstractModule implements IBackgroundModule {

    protected static _typeName: string = "background";

    protected _defaultSettings: IBackgroundSettings;

    private _backgroundSAPIElement: SkyDome | SkyBox;
    private _sourceChanged: boolean;
    private _cameraIndex: number;

    /// this is used for tiled printing when we need to draw only part of the background image
    private _backgroundImageRunTimeSettings: IBackgroundImageRuntimeSettings = {
        offsetX: 0,
        offsetY: 0,
        width: 1,
        height: 1
    };
    private _backgroundImageOffsetAndSizeChanged: boolean = false;
    private _backgroundImageEnabled: boolean = true;
    private _forceTransparentBackground: boolean = false;

    private readonly _xRayBackgroundSettings: IBackgroundSettings = {
        background: {
            color: vec3.fromValues(0, 0, 0),
            type: EBackgroundType.COLOR
        },
        backgroundImage: {
            source: '',
        }
    };
    private _xRayBackgroundSettingsPrevious: IBackgroundSettings = {};

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = BackgroundModule._typeName;

        this._defaultSettings = {
            background: {
                textureUrl: null,
                color: vec3.fromValues(143, 143, 203), // vector of 0-255 values; eg. [128, 255, 128] // default cw color [143, 143, 203]
                type: EBackgroundType.COLOR
            },
            backgroundImage: {
                name: '',
                source: '', // 'environment/grass.png',
                basePath: '',
                scaleType: EBackgroundImageFitType.FILL
            }
        };

        this._settings = Settings.clone(this._defaultSettings);
        this._backgroundSAPIElement = null;
        this._sourceChanged = false;
        this._cameraIndex = -1;
        this._dependencies = ['logo', 'hud', 'lighting']; // ['logo', 'hud', 'lighting'] // hud for compass
    }

    public static getClassType(): string {
        return BackgroundModule._typeName;
    }

    /**
     * Definition of light and dark color.
     * @param aVector3 Vector of 3 values in <0-255> range.
     */
    public static isColorLight(aVector3: vec3) {
        // exception: default lexocad griseous should give white cw logo, so it is dark
        if (aVector3[0] === 143 && aVector3[1] === 143 && aVector3[2] === 203) {
            return false;
        }

        // jscolor style
        return (
            0.213 * aVector3[0] +
            0.715 * aVector3[1] +
            0.072 * aVector3[2] >
            255 / 2
        );

        // cadwork style
        // return (
        //     (r + g + b) / 3 >
        //     255 / 2
        // );
    }

    public isBackgroundColorLight(): boolean {
        return BackgroundModule.isColorLight(this._settings.background.color);
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    public forceBlackBackground(aValue: boolean) {

        if (aValue) {
            this._xRayBackgroundSettingsPrevious = this.getModuleSettings();
            this.set(this._xRayBackgroundSettings);
            // this.setBackgroundColorInternal(vec3.fromValues(0, 0, 0));
            // this.setBackgroundTypeInternal(EBackgroundType.COLOR);
        }
        else {
            this.set(this._xRayBackgroundSettingsPrevious);
            // this.setBackgroundColorInternal(this._settings.background.color);
            // this.setBackgroundTypeInternal(this._settings.background.type);
        }

        // if (this._backgroundSAPIElement) {
        //     this._backgroundSAPIElement.setVisibility(!aValue);
        // }
    }

    // /***
    //  * Reapply the background color e.g. in case that we ar returning from xray, where the background color is forced to black
    //  */
    // public applyBackgroundColor() {
    //     this.setBackgroundColorInternal(this._settings.background.color);
    // }

    // public setSkyboxTexture(aUrl: string) {
    //     this._settings.background.textureUrl = aUrl;
    // }

    public getBackgroundType(): EBackgroundType {
        return this._settings.background.type;
    }

    /**
     * Returns a vec3 of <0, 1> values of fog color defined according to current background type.
     */
    public getFogColor(): vec3 {
        let fogColor = vec3.create();

        if (this._settings.background.type === EBackgroundType.COLOR) {
            vec3.set(fogColor,
                this._settings.background.color[0] / 255.0,
                this._settings.background.color[1] / 255.0,
                this._settings.background.color[2] / 255.0
            );
        }
        else if (this._settings.background.type === EBackgroundType.TEXTURE) {
            if (this._backgroundSAPIElement) {
                fogColor = this._backgroundSAPIElement.getFogColor();
            }
        }
        else if (this._settings.background.type === EBackgroundType.DAYNIGHT) {
            // auto set from texture gradient
            let ele = this.sceneAPI.getLightingModule().getSunElevationAngle();
            fogColor = this.sceneAPI.getLightingModule().getFog().getColorFromGradient(ele);
        }
        else if (this._settings.background.type === EBackgroundType.IMAGE) {
            vec3.set(fogColor,
                this._settings.background.color[0] / 255.0,
                this._settings.background.color[1] / 255.0,
                this._settings.background.color[2] / 255.0
            );
        }
        else if (this._settings.background.type === EBackgroundType.HWCAMERA) {
            vec3.set(fogColor,
                this._settings.background.color[0] / 255.0,
                this._settings.background.color[1] / 255.0,
                this._settings.background.color[2] / 255.0
            );
        }
        return fogColor;
    }

    /**
     * @return {boolean}
     */
    public wasBackgroundImageChanged(): boolean {
        return this._sourceChanged;
    }

    /**
     * @return {string | null}
     */
    public getBackgroundImageSource(): string {
        return this._settings.backgroundImage.source;
    }

    /**
     * @return {string | null}
     */
    public getBackgroundImageSourceBasePath(): string {
        return this._settings.backgroundImage.basePath;
    }

    /**
     * @return {number}
     */
    public getBackgroundCameraIndex(): number {
        return this._cameraIndex;
    }

    /**
     * @return {number[4]}
     */
    public getBackgroundColor(): number[] {
        return this._settings.background.color;
    }

    /**
     *
     * @returns {*}
     */
    public getBackgroundImageScaleFit(): EBackgroundImageFitType {
        return this._settings.backgroundImage.scaleType;
    }

    /**
     * @param {boolean} aValue
     */
    public setBackgroundImageChanged(aValue: boolean) {
        this._sourceChanged = aValue;
        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    /**
     * Render subimage of the background image (only runtime because of print)
     * @param offsetX 0..1
     * @param offsetY 0..1
     * @param width 0..1
     * @param height 0..1
     */
    public setBackgroundImageOffsetAndSize(aOffsetX: number, aOffsetY: number, aWidth: number, aHeight: number) {
        this._backgroundImageRunTimeSettings.offsetX = aOffsetX;
        this._backgroundImageRunTimeSettings.offsetY = aOffsetY;
        this._backgroundImageRunTimeSettings.width = aWidth;
        this._backgroundImageRunTimeSettings.height = aHeight;

        this.setBackgroundImageOffsetAndSizeChanged(true);
    }

    public setBackgroundImageOffsetAndSizeChanged(value: boolean) {
        this._backgroundImageOffsetAndSizeChanged = value;
    }

    public getBackgroundImageOffsetAndSizeChanged(): boolean {
        return this._backgroundImageOffsetAndSizeChanged;
    }

    public enableBackgroundImage(value: boolean) {
        this._backgroundImageEnabled = value;
    }

    public isBackgroundImageEnabled(): boolean {
        return this._backgroundImageEnabled;
    }

    public setTransparentBackground(value: boolean) {
        this._forceTransparentBackground = value;
    }

    public getTransparentBackground(): boolean {
        return this._forceTransparentBackground;
    }

    public getBackgroundImageOffsetAndSizeRef(): IBackgroundImageRuntimeSettings {
        return this._backgroundImageRunTimeSettings;
    }

    // todo apply settings test it for false data
    public setFromModelDescription(aJsonObject: any) {
        let settings: IBackgroundSettings = {};

        settings.background = {};

        // backward compatibility
        if (aJsonObject.hasOwnProperty("ModelConfiguration")) {
            let modelConfig: any = aJsonObject.ModelConfiguration;

            if (modelConfig.hasOwnProperty('showSkybox')) {
                settings.background.type = (modelConfig.showSkybox) ? EBackgroundType.TEXTURE : EBackgroundType.COLOR;
            }

            if (modelConfig.hasOwnProperty('backgroundType')) {
                settings.background.type = Number(modelConfig.backgroundType);
            }

            if (modelConfig.hasOwnProperty('backgroundImageName')) {
                settings.backgroundImage = {};
                settings.backgroundImage.basePath = "";
                settings.backgroundImage.source = this.sceneDocument.getMainModel().getBaseUrl() + modelConfig.backgroundImageName;
            }

            if (modelConfig.hasOwnProperty('backgroundImageFitType')) {
                settings.backgroundImage.scaleType = modelConfig.backgroundImageFitType === 0 ? EBackgroundImageFitType.FILL : EBackgroundImageFitType.FIT;
            }

            if (modelConfig.hasOwnProperty('backgroundColor')) {
                settings.background.color = vec3.fromValues(
                    // see https://stackoverflow.com/questions/48886499/rgb-colour-from-0-1-to-0-255-conversion-intensity-probability-distribution
                    Math.floor(modelConfig.backgroundColor[0] * 256),
                    Math.floor(modelConfig.backgroundColor[1] * 256),
                    Math.floor(modelConfig.backgroundColor[2] * 256));
            }
        }
        // end backward compatibility

        if (aJsonObject.hasOwnProperty(this.getType())) {
            settings = aJsonObject[this.getType()];
        }

        this.set(settings);
    }

    protected applySettings(aChangedSettings: any, aPreviousSettings: any) {

        if (aChangedSettings) {
            if (aChangedSettings.hasOwnProperty('background')) {
                let bg = aChangedSettings['background'];

                if (bg.hasOwnProperty('textureUrl')) {
                    if (bg.textureUrl !== '') {
                        // there should be probably some check...
                        // not possible now
                    }
                }

                if (bg.hasOwnProperty('color')) {
                    // this.setBackgroundColorInternal(bg.color);
                    this.sceneAPI.viewModeModule.scheduleRedraw();
                }

                if (bg.hasOwnProperty('type')) {
                    this.setBackgroundTypeInternal(bg.type);
                }
            }
            if (aChangedSettings.hasOwnProperty('backgroundImage')) {
                let bgi = aChangedSettings['backgroundImage'];

                if (bgi.hasOwnProperty('basePath')) {
                    this.setBackgroundImageChanged(true);
                }

                if (bgi.hasOwnProperty('source')) {
                    if (bgi.source !== '') {
                        this.setBackgroundImageChanged(true);
                    }
                }

                if (bgi.hasOwnProperty('scaleType')) {
                    if (bgi.scaleType !== '') {
                        this.setBackgroundImageChanged(true);
                    }
                }
            }
        }
    }

    /**
     * @param {number} aValue
     */
    private setBackgroundCameraIndex(aValue: number) {
        this._cameraIndex = aValue;
        this.setBackgroundImageChanged(true);
    }

    private setBackgroundTypeInternal(aState: EBackgroundType) {
        // this shall not happen
        if (this._backgroundSAPIElement) {
            this._backgroundSAPIElement.remove();
            this._backgroundSAPIElement = null;
        }

        if (aState === EBackgroundType.TEXTURE) {
            this._backgroundSAPIElement = this.sceneDocument.createAPIElement(SkyBox.TypeName) as SkyBox;
            this.sceneDocument.attachElementToInternalModel(this._backgroundSAPIElement);
            if (this._settings.background.textureUrl) {
                this._backgroundSAPIElement.setTextureUrl(this._settings.background.textureUrl);
            }
            this._backgroundSAPIElement.draw(true);
            // this._backgroundSAPIElement.setVisibility(!this.sceneAPI.getRenderSettingsModule().getRenderXRay());
        }
        else if (aState === EBackgroundType.DAYNIGHT) {
            this._backgroundSAPIElement = this.sceneDocument.createAPIElement(SkyDome.TypeName) as SkyDome;
            this.sceneDocument.attachElementToInternalModel(this._backgroundSAPIElement);
            this._backgroundSAPIElement.set(450000, 32, 15, undefined, undefined, undefined, undefined); // 3js
            this._backgroundSAPIElement.draw(true);
            // this._backgroundSAPIElement.setVisibility(!this.sceneAPI.getRenderSettingsModule().getRenderXRay());

            this.sceneAPI.lightingModule.set({
                sunlight: {
                    sunlightIntensity: 1,
                    headLightIntensity: 0,
                    handling: ESunDirectionType.DATETIME
                }
            });
        }
        else if (aState === EBackgroundType.IMAGE) {
            this.setBackgroundImageChanged(true);
        }
        else if (aState === EBackgroundType.HWCAMERA) {
            // Test if camera is supported
            if (!this.sceneAPI.hwcamerasimpleModule.isSupported()) {
                aState = this._settings.background.type;
                // this._settings.background.type = aState;
                Debug.logWarning("BackgroundModule: HW Camera is not supported!");
            } else {
                this.setBackgroundImageChanged(true);
            }
        }
    }
}

Scene.registerSceneAPIModule(BackgroundModule);
